// src/Components/Modules/CRM/CrmLeads.js
import React, { useState, useEffect } from "react";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import Breadcrumb from "../../Partials/Breadcrumb";
import {
  Box, Typography, Paper, Grid, Button, IconButton, Chip,
  Dialog, DialogTitle, DialogContent, DialogActions, TextField,
  MenuItem, Select, FormControl, InputLabel, Tooltip, Skeleton,
  Stack, Avatar, Divider, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TablePagination
} from "@mui/material";
import {
  FaPlus, FaEdit, FaTrash, FaUsers, FaSync, FaUserPlus, FaEye
} from "react-icons/fa";
import { styled } from "@mui/material/styles";
import Swal from "sweetalert2";

const HeaderPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(3),
  marginBottom: theme.spacing(3),
  background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  color: "white",
  borderRadius: theme.spacing(2),
  boxShadow: "0 10px 40px rgba(102, 126, 234, 0.3)",
}));

const CrmLeads = () => {
  const [leads, setLeads] = useState([]);
  const [sources, setSources] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [editingLead, setEditingLead] = useState(null);
  const [convertingLead, setConvertingLead] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [form, setForm] = useState({
    title: "", description: "", source_id: "", assigned_to: "",
    estimated_value: 0, expected_close_date: "", contacts: [{ name: "", email: "", phone: "", designation: "" }]
  });
  const [convertForm, setConvertForm] = useState({ customer_name: "", email: "", phone: "", address: "" });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [leadsRes, sourcesRes, usersRes] = await Promise.all([
        AxiosAuthInstance.get("crm/leads"),
        AxiosAuthInstance.get("crm/lead-sources"),
        AxiosAuthInstance.get("hr/projects/users/assignable"), // Reusing existing endpoint
      ]);
      setLeads(leadsRes.data.data || []);
      setStats(leadsRes.data.stats || {});
      setSources(sourcesRes.data || []);
      setUsers(usersRes.data.data || []);
    } catch (err) {
      Swal.fire("Error", "Failed to load leads", "error");
    } finally {
      setLoading(false);
    }
  };

  const openDialog = (lead = null) => {
    if (lead) {
      setEditingLead(lead);
      setForm({
        title: lead.title,
        description: lead.description || "",
        source_id: lead.source_id || "",
        assigned_to: lead.assigned_to || "",
        estimated_value: lead.estimated_value || 0,
        expected_close_date: lead.expected_close_date || "",
        contacts: lead.contacts?.length > 0 ? lead.contacts : [{ name: "", email: "", phone: "", designation: "" }]
      });
    } else {
      setEditingLead(null);
      setForm({ title: "", description: "", source_id: "", assigned_to: "", estimated_value: 0, expected_close_date: "", contacts: [{ name: "", email: "", phone: "", designation: "" }] });
    }
    setDialogOpen(true);
  };

  const handleContactChange = (index, field, value) => {
    const newContacts = [...form.contacts];
    newContacts[index][field] = value;
    setForm({ ...form, contacts: newContacts });
  };

  const addContactField = () => {
    setForm({ ...form, contacts: [...form.contacts, { name: "", email: "", phone: "", designation: "" }] });
  };

  const removeContactField = (index) => {
    const newContacts = form.contacts.filter((_, i) => i !== index);
    setForm({ ...form, contacts: newContacts });
  };

  const handleSubmit = async () => {
    if (!form.title.trim()) {
      Swal.fire("Warning", "Lead title is required", "warning");
      return;
    }
    try {
      if (editingLead) {
        await AxiosAuthInstance.put(`crm/leads/${editingLead.id}`, form);
        Swal.fire({ icon: "success", title: "Updated!", timer: 1500, showConfirmButton: false });
      } else {
        await AxiosAuthInstance.post("crm/leads", form);
        Swal.fire({ icon: "success", title: "Created!", timer: 1500, showConfirmButton: false });
      }
      setDialogOpen(false);
      fetchData();
    } catch (err) {
      Swal.fire("Error", err.response?.data?.message || "Operation failed", "error");
    }
  };

  const openConvertDialog = (lead) => {
    setConvertingLead(lead);
    const primaryContact = lead.contacts?.find(c => c.is_primary) || lead.contacts?.[0] || {};
    setConvertForm({
      customer_name: lead.title,
      email: primaryContact.email || "",
      phone: primaryContact.phone || "",
      address: ""
    });
    setConvertDialogOpen(true);
  };

  const handleConvert = async () => {
    if (!convertForm.customer_name.trim()) {
      Swal.fire("Warning", "Customer name is required", "warning");
      return;
    }
    try {
      await AxiosAuthInstance.post(`crm/leads/${convertingLead.id}/convert`, convertForm);
      Swal.fire({ icon: "success", title: "Converted!", text: "Lead successfully converted to customer", timer: 2000, showConfirmButton: false });
      setConvertDialogOpen(false);
      fetchData();
    } catch (err) {
      Swal.fire("Error", err.response?.data?.message || "Conversion failed", "error");
    }
  };

  const handleDelete = async (lead) => {
    const result = await Swal.fire({
      title: "Delete Lead?",
      text: `"${lead.title}" will be permanently deleted.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#f44336",
      confirmButtonText: "Yes, Delete",
    });
    if (result.isConfirmed) {
      try {
        await AxiosAuthInstance.delete(`crm/leads/${lead.id}`);
        Swal.fire("Deleted!", "Lead deleted", "success");
        fetchData();
      } catch (err) {
        Swal.fire("Error", "Failed to delete", "error");
      }
    }
  };

  const getStatusChip = (status) => {
    const colors = {
      new: { bg: "#e3f2fd", color: "#1976d2" },
      contacted: { bg: "#fff3e0", color: "#ef6c00" },
      qualified: { bg: "#f3e5f5", color: "#7b1fa2" },
      converted: { bg: "#e8f5e9", color: "#2e7d32" },
      lost: { bg: "#ffebee", color: "#c62828" },
    };
    const c = colors[status] || colors.new;
    return <Chip label={status} size="small" sx={{ bgcolor: c.bg, color: c.color, fontWeight: 600 }} />;
  };

  const formatCurrency = (value) => value ? "৳" + Number(value).toLocaleString("en-BD") : "৳0";

  return (
    <Box sx={{ p: 3, bgcolor: "#f5f7fa", minHeight: "100vh" }}>
      <Breadcrumb title="CRM Leads" />

      <HeaderPaper elevation={0}>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 2 }}>
          <Box>
            <Typography variant="h4" fontWeight={700} gutterBottom>
              <FaUsers style={{ marginRight: 10, verticalAlign: "middle" }} />
              Lead Management
            </Typography>
            <Typography variant="body1" sx={{ opacity: 0.9 }}>
              Track and convert potential customers
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<FaPlus />}
            onClick={() => openDialog()}
            sx={{ bgcolor: "white", color: "#667eea", fontWeight: 600, "&:hover": { bgcolor: "#f0f0f0" }, px: 3, py: 1.5, borderRadius: 2, textTransform: "none" }}
          >
            New Lead
          </Button>
        </Box>

        <Box sx={{ display: "flex", gap: 2, mt: 3, flexWrap: "wrap" }}>
          {[
            { label: "Total", value: stats.total || 0 },
            { label: "New", value: stats.new || 0 },
            { label: "Qualified", value: stats.qualified || 0 },
            { label: "Converted", value: stats.converted || 0 },
            { label: "Total Value", value: formatCurrency(stats.total_value) },
          ].map((s, i) => (
            <Paper key={i} sx={{ p: 2, bgcolor: "rgba(255,255,255,0.15)", color: "white", borderRadius: 2, minWidth: 120, flex: 1 }}>
              <Typography variant="caption" sx={{ opacity: 0.9 }}>{s.label}</Typography>
              <Typography variant="h5" fontWeight={700}>{s.value}</Typography>
            </Paper>
          ))}
        </Box>
      </HeaderPaper>

      <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#fafafa" }}>
                <TableCell>Title</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Assigned To</TableCell>
                <TableCell>Value</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={6}><Skeleton height={60} /></TableCell></TableRow>
              ) : leads.length === 0 ? (
                <TableRow><TableCell colSpan={6} align="center" sx={{ py: 6 }}><Typography color="text.secondary">No leads found</Typography></TableCell></TableRow>
              ) : (
                leads.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((lead) => (
                  <TableRow key={lead.id} hover>
                    <TableCell>
                      <Typography variant="subtitle2" fontWeight={600}>{lead.title}</Typography>
                      <Typography variant="caption" color="text.secondary">{lead.expected_close_date && `Close: ${lead.expected_close_date}`}</Typography>
                    </TableCell>
                    <TableCell>{lead.source?.name || "—"}</TableCell>
                    <TableCell>{lead.assignee?.name || "Unassigned"}</TableCell>
                    <TableCell>{formatCurrency(lead.estimated_value)}</TableCell>
                    <TableCell>{getStatusChip(lead.status)}</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Edit"><IconButton size="small" onClick={() => openDialog(lead)} sx={{ color: "#ff9800" }}><FaEdit size={14} /></IconButton></Tooltip>
                      {lead.status !== 'converted' && (
                        <Tooltip title="Convert to Customer"><IconButton size="small" onClick={() => openConvertDialog(lead)} sx={{ color: "#4caf50" }}><FaUserPlus size={14} /></IconButton></Tooltip>
                      )}
                      <Tooltip title="Delete"><IconButton size="small" onClick={() => handleDelete(lead)} sx={{ color: "#f44336" }}><FaTrash size={14} /></IconButton></Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={leads.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        />
      </Paper>

      {/* Lead Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: "#667eea", color: "white" }}>{editingLead ? "Edit Lead" : "Create New Lead"}</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={2}>
            <TextField fullWidth label="Lead Title *" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            <TextField fullWidth label="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} multiline rows={3} />
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Source</InputLabel>
                  <Select value={form.source_id} label="Source" onChange={(e) => setForm({ ...form, source_id: e.target.value })}>
                    <MenuItem value="">None</MenuItem>
                    {sources.map(s => <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormControl fullWidth>
                  <InputLabel>Assign To</InputLabel>
                  <Select value={form.assigned_to} label="Assign To" onChange={(e) => setForm({ ...form, assigned_to: e.target.value })}>
                    <MenuItem value="">Unassigned</MenuItem>
                    {users.map(u => <MenuItem key={u.id} value={u.id}>{u.name}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Estimated Value" type="number" value={form.estimated_value} onChange={(e) => setForm({ ...form, estimated_value: e.target.value })} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Expected Close Date" type="date" value={form.expected_close_date} onChange={(e) => setForm({ ...form, expected_close_date: e.target.value })} InputLabelProps={{ shrink: true }} />
              </Grid>
            </Grid>

            <Divider sx={{ my: 1 }} />
            <Typography variant="subtitle1" fontWeight={600}>Contact Persons</Typography>
            {form.contacts.map((contact, index) => (
              <Paper key={index} sx={{ p: 2, bgcolor: "#f8f9fa" }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} md={3}><TextField size="small" fullWidth label="Name" value={contact.name} onChange={(e) => handleContactChange(index, "name", e.target.value)} /></Grid>
                  <Grid item xs={12} md={3}><TextField size="small" fullWidth label="Email" value={contact.email} onChange={(e) => handleContactChange(index, "email", e.target.value)} /></Grid>
                  <Grid item xs={12} md={3}><TextField size="small" fullWidth label="Phone" value={contact.phone} onChange={(e) => handleContactChange(index, "phone", e.target.value)} /></Grid>
                  <Grid item xs={12} md={2}><TextField size="small" fullWidth label="Designation" value={contact.designation} onChange={(e) => handleContactChange(index, "designation", e.target.value)} /></Grid>
                  <Grid item xs={12} md={1}>
                    {form.contacts.length > 1 && <IconButton size="small" onClick={() => removeContactField(index)} color="error"><FaTrash /></IconButton>}
                  </Grid>
                </Grid>
              </Paper>
            ))}
            <Button size="small" startIcon={<FaPlus />} onClick={addContactField} sx={{ textTransform: "none" }}>Add Another Contact</Button>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSubmit} sx={{ bgcolor: "#667eea" }}>{editingLead ? "Update" : "Create"}</Button>
        </DialogActions>
      </Dialog>

      {/* Convert Dialog */}
      <Dialog open={convertDialogOpen} onClose={() => setConvertDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: "#4caf50", color: "white" }}>Convert Lead to Customer</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Converting <strong>{convertingLead?.title}</strong> will create a new customer record and mark this lead as converted.
          </Typography>
          <Stack spacing={2}>
            <TextField fullWidth label="Customer Name *" value={convertForm.customer_name} onChange={(e) => setConvertForm({ ...convertForm, customer_name: e.target.value })} />
            <TextField fullWidth label="Email" value={convertForm.email} onChange={(e) => setConvertForm({ ...convertForm, email: e.target.value })} />
            <TextField fullWidth label="Phone" value={convertForm.phone} onChange={(e) => setConvertForm({ ...convertForm, phone: e.target.value })} />
            <TextField fullWidth label="Address" value={convertForm.address} onChange={(e) => setConvertForm({ ...convertForm, address: e.target.value })} multiline rows={2} />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setConvertDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleConvert} sx={{ bgcolor: "#4caf50" }}>Convert</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CrmLeads;